import { useState, useRef, useEffect } from 'react';

interface MusicCardProps {
  musicFileName: string;
  title?: string;
  artist?: string;
  isPlaying?: boolean;
  onPlay?: () => void;
  onStop?: () => void;
  size?: 'small' | 'medium' | 'large';
}

export default function MusicCard({
  musicFileName,
  title = 'Untitled Soundtrack',
  artist = 'Unknown Artist',
  isPlaying = false,
  onPlay,
  onStop,
  size = 'medium',
}: MusicCardProps) {
  const [isPlayingLocal, setIsPlayingLocal] = useState(isPlaying);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLooping, setIsLooping] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setIsPlayingLocal(isPlaying);
  }, [isPlaying]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    if (isPlayingLocal) {
      audioElement.play().catch(() => {
        setIsPlayingLocal(false);
      });
    } else {
      audioElement.pause();
    }

    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [isPlayingLocal]);

  const togglePlay = () => {
    if (onPlay && onStop) {
      if (isPlaying) {
        onStop();
      } else {
        onPlay();
      }
    } else {
      setIsPlayingLocal(!isPlayingLocal);
    }
  };

  const handleEnded = () => {
    if (onStop) {
      onStop();
    } else {
      setIsPlayingLocal(false);
    }
  };

  const toggleLoop = () => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const newLoopState = !audioElement.loop;
      audioElement.loop = newLoopState;
      setIsLooping(newLoopState);
    }
  };

  const handleProgressClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const audioElement = audioRef.current;
    if (audioElement && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newTime = ((e.clientX - rect.left) / rect.width) * duration;
      audioElement.currentTime = Math.max(0, Math.min(newTime, duration));
    }
  };

  const formatTime = (time: number) => {
    if (!isFinite(time) || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="group flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-900 transition-colors duration-200">
      {/* Dot / Play Button */}
      <div className="w-8 flex justify-center items-center shrink-0">
        <button
          onClick={togglePlay}
          aria-label={isPlayingLocal ? 'Pause' : 'Play'}
          className="text-custom-stone hover:text-custom-stone/80 transition-colors duration-200"
        >
          {isPlayingLocal ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
        </button>
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-custom-stone truncate">
          {title}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{artist}</p>

        {/* Progress Bar */}
        <div className="mt-2">
          <div
            className="w-full h-1 bg-gray-700 rounded-full cursor-pointer"
            onClick={handleProgressClick}
            role="progressbar"
            aria-valuenow={(currentTime / duration) * 100 || 0}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-1 bg-custom-stone rounded-full transition-all duration-100"
              style={{
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
              }}
            />
          </div>
          <span className="text-xs text-gray-500 mt-1 block tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Sound Bars */}
      <div className="flex items-center gap-0.5 shrink-0">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="w-1 h-4 bg-custom-stone/70 rounded-full"
            style={{
              animation: `pulse ${0.5 + i * 0.1}s ease-in-out infinite`,
              animationPlayState: isPlayingLocal ? 'running' : 'paused',
            }}
          />
        ))}
      </div>

      {/* Loop Button */}
      <div className="relative group/loop shrink-0">
        <button
          onClick={toggleLoop}
          aria-label={isLooping ? 'Loop On' : 'Loop Off'}
          aria-pressed={isLooping}
          className={`transition-colors duration-200 active:scale-90 ${
            isLooping
              ? 'text-custom-stone hover:text-custom-stone/80'
              : 'text-gray-600 hover:text-gray-400'
          }`}
        >
          <svg
            className="w-5 h-5"
            viewBox="0 0 32 25"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M23.5 16C22 20 18 23 14 23C8.5 23 4 19 3.3 13.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 10C5.5 6 9 2.5 14 2.5C19.5 2.5 24 7 24.5 12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M24.5 23L24.5 17.5C24.5 17.2 24.2 17 23.9 17H17"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 10.5H4C3.7 10.5 3.5 10.2 3.5 9.5V4"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover/loop:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
          {isLooping ? 'Loop On' : 'Loop Off'}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800" />
        </div>
      </div>

      <audio
        ref={audioRef}
        src={`/assets/music/${musicFileName}`}
        onEnded={handleEnded}
        preload="metadata"
      />
    </div>
  );
}
